import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  FiCheck,
  FiCheckCircle,
  FiCalendar,
  FiBookOpen,
  FiArrowRight,
  FiLogIn,
} from "react-icons/fi";
import { useModal } from "../../context/Modal";
import LoginForm from "../LoginForm";

const ONE_YEAR_SLUG = "one-year-2026";

// ---------- Shared glass surfaces --------------------------------------

const glassCard = {
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 55%, rgba(255,255,255,0.15) 100%)",
  backdropFilter: "blur(22px) saturate(108%)",
  WebkitBackdropFilter: "blur(22px) saturate(108%)",
  border: "1px solid rgba(255,255,255,0.45)",
  boxShadow:
    "0 18px 44px rgba(55,79,99,0.13), inset 0 1px 0 rgba(255,255,255,0.55)",
};

const innerTranslucent = {
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0.35))",
  border: "1px solid rgba(255,255,255,0.45)",
};

const primaryPill =
  "inline-flex items-center gap-2 h-11 px-space-md rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all";
const secondaryPill = `inline-flex items-center gap-2 h-11 px-space-md rounded-full bg-white/60 text-neu-primary text-label-md font-semibold border border-white/60 hover:bg-white/80 active:bg-white/40 transition-all`;

// ---------- Helpers ----------------------------------------------------

const clientTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (_e) {
    return null;
  }
};

const isoOf = (y, m, d) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const fullDate = (dateISO) =>
  dateISO
    ? new Date(`${dateISO}T00:00:00`).toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";

// Sun -> Sat window around a given ISO date. We label the strip Mon → Sun,
// so shift the anchor accordingly.
const mondayWeek = (anchorISO) => {
  const src = anchorISO ? new Date(`${anchorISO}T00:00:00`) : new Date();
  const dow = src.getDay();
  const daysSinceMon = (dow + 6) % 7;
  const monday = new Date(src);
  monday.setDate(src.getDate() - daysSinceMon);
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    out.push({
      iso: isoOf(d.getFullYear(), d.getMonth() + 1, d.getDate()),
      long: d.toLocaleDateString(undefined, { weekday: "short" }),
      short: d.toLocaleDateString(undefined, { weekday: "narrow" }),
      dayNum: d.getDate(),
    });
  }
  return { monday: out[0].iso, sunday: out[6].iso, days: out };
};

// ---------- Component -------------------------------------------------

const ReadingPlansHub = () => {
  const user = useSelector((state) => state.session.user);
  const { i18n } = useTranslation();
  const { setModalContent } = useModal();
  const openLoginModal = () => setModalContent(<LoginForm />);
  const language = i18n.language?.startsWith("ko") ? "ko" : "en";
  const pickRef = (en, ko) =>
    language === "ko" ? ko || en : en || ko;

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("bibleMemosTheme") || "powder";
    } catch (_e) {
      return "powder";
    }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bibleMemosTheme" && e.newValue) setTheme(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const tz = useMemo(clientTz, []);
  const commonHeaders = useMemo(
    () => (tz ? { "X-Client-Timezone": tz } : {}),
    [tz]
  );

  // Devotional today.
  const [devotion, setDevotion] = useState({ loading: true, day: null });
  useEffect(() => {
    let cancelled = false;
    fetch("/api/devotions/today", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((day) => { if (!cancelled) setDevotion({ loading: false, day }); })
      .catch(() => { if (!cancelled) setDevotion({ loading: false, day: null }); });
    return () => { cancelled = true; };
  }, []);

  // Devotional calendar (current month + next month if we're near end).
  const [devotionCal, setDevotionCal] = useState({});
  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const loadMonth = (y, m) =>
      fetch(`/api/devotions/calendar?year=${y}&month=${m}&language=en`, {
        credentials: "include",
      })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    (async () => {
      const first = await loadMonth(year, month);
      const map = {};
      (first?.days || []).forEach((d) => { map[d.date] = d; });
      if (now.getDate() > 24) {
        const next = new Date(year, month, 1);
        const second = await loadMonth(next.getFullYear(), next.getMonth() + 1);
        (second?.days || []).forEach((d) => { map[d.date] = d; });
      }
      setDevotionCal(map);
    })();
  }, []);

  // One-year plan meta + progress + today.
  const [plan, setPlan] = useState({ loading: true, meta: null, today: null, progress: null });
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(`/api/reading-plans/${ONE_YEAR_SLUG}`, { headers: commonHeaders, credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
      fetch(`/api/reading-plans/${ONE_YEAR_SLUG}/today`, { headers: commonHeaders, credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
      fetch(`/api/reading-plans/${ONE_YEAR_SLUG}/progress`, { headers: commonHeaders, credentials: "include" }).then((r) => (r.ok ? r.json() : null)),
    ]).then(([meta, today, progress]) => {
      if (!cancelled) setPlan({ loading: false, meta, today, progress });
    });
    return () => { cancelled = true; };
  }, [commonHeaders]);

  // Compute this-week window off today's ISO.
  const todayAnchor = devotion.day?.passage?.passage_date || plan.today?.date || null;
  const week = useMemo(() => mondayWeek(todayAnchor), [todayAnchor]);
  const todayISO = week.days.find((d) => d.iso === todayAnchor)?.iso || todayAnchor;

  // Fetch this-week's one-year segments so we can (a) list today's segments in
  // the annual card and (b) render the combined weekly tracker.
  const [weekPlanDays, setWeekPlanDays] = useState({});
  useEffect(() => {
    if (!week.monday || !week.sunday) return;
    fetch(
      `/api/reading-plans/${ONE_YEAR_SLUG}/days?start=${week.monday}&end=${week.sunday}`,
      { headers: commonHeaders, credentials: "include" }
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const map = {};
        (rows || []).forEach((row) => {
          if (!map[row.date]) map[row.date] = [];
          map[row.date].push(row);
        });
        Object.values(map).forEach((arr) => arr.sort((a, b) => a.sequence - b.sequence));
        setWeekPlanDays(map);
      })
      .catch(() => setWeekPlanDays({}));
  }, [week.monday, week.sunday, commonHeaders]);

  const completedDayIds = useMemo(
    () => new Set(plan.progress?.completed_day_ids || []),
    [plan.progress]
  );

  const planDateComplete = (dateISO) => {
    const segs = weekPlanDays[dateISO];
    if (!segs || !segs.length) return null;
    return segs.every((s) => completedDayIds.has(s.id));
  };

  // Today's segments (for the annual card list).
  const todaySegments = weekPlanDays[todayISO] || [];

  const devotionPassageRef = devotion.day?.passage
    ? {
        en: devotion.day.passage.reference_en,
        ko: devotion.day.passage.reference_ko,
      }
    : null;
  const devotionCompleted = Boolean(devotion.day?.entry?.completed_at);
  const devotionDate = devotion.day?.passage?.passage_date;

  const planCompletedCount = plan.progress?.completed_count ?? 0;
  const planTotalCount = plan.progress?.total_count ?? 365;
  const planPercent = plan.progress?.percent ?? 0;

  // Section heading date — fall back to today's device date if neither plan
  // returned yet.
  const headingISO =
    todayISO ||
    devotionDate ||
    isoOf(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate());

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors"
    >
      <main className="w-full">
        {/* Content width tightened to ~1180 per the review. */}
        <div className="w-full max-w-[1200px] mx-auto px-margin-mobile md:px-margin-neu py-space-xl flex flex-col gap-space-lg">
          {/* ------ Compact heading (no hero card) ------ */}
          <div className="flex flex-col items-center text-center gap-1">
            <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
              Reading Plans
            </span>
            <h1
              className="font-noto-serif text-neu-primary whitespace-nowrap"
              style={{ fontSize: "clamp(1.5rem, 7vw, 2rem)", lineHeight: 1.2 }}
            >
              Two rhythms, one Word
            </h1>
            <p className="text-body-md text-on-surface-variant max-w-xl">
              Follow today's devotional and journey through the whole Bible.
            </p>
          </div>

          {/* ------ Today · <date> section heading ------ */}
          <div className="flex items-center gap-3">
            <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
              Today
            </span>
            <span className="text-body-md text-on-surface-variant">
              · {fullDate(headingISO)}
            </span>
          </div>

          {/* ------ Two plan cards ------ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-gutter items-stretch">
            {/* Daily Devotional */}
            <article
              className="rounded-[2rem] p-space-lg md:p-space-xl flex flex-col gap-space-md"
              style={glassCard}
            >
              <div className="flex items-start justify-between gap-space-sm">
                <div className="flex items-center gap-space-sm">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center" style={innerTranslucent}>
                    <FiBookOpen className="text-accent-cta text-[20px]" />
                  </div>
                  <div>
                    <p className="text-label-sm uppercase tracking-wider text-accent-gold-deep font-semibold">
                      Daily Devotional
                    </p>
                    <p className="text-label-sm text-on-surface-variant">매일성경</p>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-space-sm py-1 rounded-full text-label-sm font-semibold ${
                    devotionCompleted ? "text-accent-cta" : "text-on-surface-variant"
                  }`}
                  style={innerTranslucent}
                >
                  {devotionCompleted ? <><FiCheckCircle /> Complete</> : "Today"}
                </span>
              </div>

              <div className="flex flex-col gap-0.5">
                <h2 className="font-noto-serif text-headline-md text-neu-primary">
                  {devotion.loading
                    ? "Loading…"
                    : pickRef(devotionPassageRef?.en, devotionPassageRef?.ko) ||
                      "No devotional scheduled"}
                </h2>
              </div>

              <p className="text-body-neu-md text-on-surface-variant">
                Pastoral guidance, reflection and prayer.
              </p>

              <div className="mt-auto pt-space-sm flex flex-wrap items-center gap-space-sm">
                <Link
                  to={devotionDate ? `/devotions/date/${devotionDate}` : "/devotions/today"}
                  className={primaryPill}
                >
                  <FiBookOpen /><span>Open Today</span>
                </Link>
                {user ? (
                  <Link to="/my-memos/devotions" className={secondaryPill}>
                    <FiCalendar /><span>Schedule</span>
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={openLoginModal}
                    className={secondaryPill}
                  >
                    <FiLogIn /><span>Sign in to track</span>
                  </button>
                )}
              </div>
            </article>

            {/* One-Year Bible */}
            <article
              className="rounded-[2rem] p-space-lg md:p-space-xl flex flex-col gap-space-md"
              style={glassCard}
            >
              <div className="flex items-start justify-between gap-space-sm">
                <div className="flex items-center gap-space-sm">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center" style={innerTranslucent}>
                    <FiCalendar className="text-accent-cta text-[20px]" />
                  </div>
                  <div>
                    <p className="text-label-sm uppercase tracking-wider text-accent-gold-deep font-semibold">
                      One-Year Bible
                    </p>
                    <p className="text-label-sm text-on-surface-variant">일년일독</p>
                  </div>
                </div>
                <span
                  className="inline-flex items-center px-space-sm py-1 rounded-full text-label-sm font-semibold text-neu-primary"
                  style={innerTranslucent}
                >
                  {!user
                    ? "Sign in"
                    : planCompletedCount === 0
                      ? "Start today"
                      : `${planCompletedCount} / ${planTotalCount}`}
                </span>
              </div>

              <div className="flex flex-col gap-0.5">
                {todaySegments.length ? (
                  todaySegments.map((seg, i) => (
                    <h2
                      key={seg.id}
                      className={`font-noto-serif ${
                        i === 0
                          ? "text-headline-md text-neu-primary"
                          : "text-headline-sm text-on-surface-variant"
                      }`}
                    >
                      {pickRef(seg.reference_en, seg.reference_ko)}
                    </h2>
                  ))
                ) : (
                  <h2 className="font-noto-serif text-headline-md text-neu-primary">
                    {plan.loading
                      ? "Loading…"
                      : pickRef(plan.today?.reference_en, plan.today?.reference_ko) ||
                        plan.meta?.name ||
                        "No plan"}
                  </h2>
                )}
              </div>

              <div className="pt-space-xs">
                <div className="flex justify-between text-label-sm text-on-surface-variant mb-1.5">
                  <span>Annual progress</span>
                  <span>
                    {user
                      ? `${planCompletedCount} of ${planTotalCount} days · ${planPercent}%`
                      : `0 of ${planTotalCount} days`}
                  </span>
                </div>
                <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.35)", boxShadow: "inset 0 1px 2px rgba(31,41,55,0.10)" }}>
                  <div
                    className="h-full rounded-full bg-accent-cta transition-all"
                    style={{ width: `${planPercent}%` }}
                  />
                </div>
              </div>

              <div className="mt-auto pt-space-sm flex flex-wrap items-center gap-space-sm">
                {user ? (
                  <Link
                    to={todayISO ? `/bible-reading/plan/${ONE_YEAR_SLUG}/${todayISO}` : "/bible-reading"}
                    className={primaryPill}
                  >
                    <FiArrowRight /><span>Read Today</span>
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={openLoginModal}
                    className={primaryPill}
                  >
                    <FiLogIn /><span>Sign in to Read</span>
                  </button>
                )}
                {user ? (
                  <Link to={`/reading-plans/${ONE_YEAR_SLUG}`} className={secondaryPill}>
                    <FiCalendar /><span>Full Plan</span>
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={openLoginModal}
                    className={secondaryPill}
                  >
                    <FiCalendar /><span>Full Plan</span>
                  </button>
                )}
              </div>
            </article>
          </div>

          {/* ------ This Week combined tracker ------ */}
          <div className="flex items-center gap-3 mt-space-sm flex-wrap">
            <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
              This Week
            </span>
            <span className="text-body-md text-on-surface-variant">
              · {week.monday} → {week.sunday}
            </span>
            {!user && (
              <button
                type="button"
                onClick={openLoginModal}
                className="ml-auto inline-flex items-center gap-1 text-label-md font-semibold text-accent-cta hover:underline"
              >
                <FiLogIn aria-hidden="true" />
                Sign in to save progress
              </button>
            )}
          </div>

          <section
            className="rounded-[2rem] p-space-lg overflow-x-auto"
            style={glassCard}
          >
            <table className="w-full min-w-[520px] text-center">
              <thead>
                <tr>
                  <th className="text-left text-label-sm text-on-surface-variant font-semibold pb-space-sm">
                    Plan
                  </th>
                  {week.days.map((d) => {
                    const isToday = d.iso === todayISO;
                    return (
                      <th
                        key={d.iso}
                        className={`pb-space-sm text-label-sm font-semibold ${
                          isToday ? "text-accent-cta" : "text-on-surface-variant"
                        }`}
                      >
                        <div className="flex flex-col items-center">
                          <span>{d.long}</span>
                          <span className="text-neu-primary/70 text-[10px] font-normal">
                            {d.dayNum}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {/* Daily Devotional row */}
                <TrackerRow
                  label="Daily Devotional"
                  sublabel="매일성경"
                  days={week.days}
                  todayISO={todayISO}
                  statusFor={(iso) => devotionCal[iso]?.status || null}
                  linkFor={(iso) => `/devotions/date/${iso}`}
                />
                {/* One-Year Bible row */}
                <TrackerRow
                  label="One-Year Bible"
                  sublabel="일년일독"
                  days={week.days}
                  todayISO={todayISO}
                  statusFor={(iso) => {
                    const state = planDateComplete(iso);
                    if (state === null) return null;
                    return state ? "complete" : "scheduled";
                  }}
                  linkFor={(iso) => (weekPlanDays[iso] ? `/bible-reading/plan/${ONE_YEAR_SLUG}/${iso}` : null)}
                />
              </tbody>
            </table>
          </section>
        </div>
      </main>
    </div>
  );
};

// One row of the combined weekly tracker.
const TrackerRow = ({ label, sublabel, days, todayISO, statusFor, linkFor }) => (
  <tr>
    <td className="text-left align-middle py-2">
      <p className="text-label-md font-semibold text-neu-primary">{label}</p>
      <p className="text-label-sm text-on-surface-variant">{sublabel}</p>
    </td>
    {days.map((d) => {
      const status = statusFor(d.iso); // 'complete' | 'started' | 'scheduled' | null
      const isToday = d.iso === todayISO;
      const done = status === "complete";
      const started = status === "started";
      const to = linkFor(d.iso);
      const cell = (
        <div
          className={`mx-auto w-8 h-8 rounded-full flex items-center justify-center text-label-sm ${
            done
              ? "bg-accent-cta text-white"
              : isToday
                ? "text-accent-cta border-2 border-accent-cta"
                : status
                  ? "text-on-surface-variant"
                  : "text-on-surface-variant/40"
          }`}
          style={
            done
              ? undefined
              : isToday
                ? undefined
                : status
                  ? { background: "rgba(255,255,255,0.35)", border: "1px solid rgba(255,255,255,0.45)" }
                  : { background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)" }
          }
          aria-label={
            done ? `${d.iso}, complete` : isToday ? `${d.iso}, today` : started ? `${d.iso}, started` : `${d.iso}`
          }
        >
          {done ? <FiCheck size={14} /> : started ? "·" : isToday ? "" : ""}
        </div>
      );
      return (
        <td key={d.iso} className="align-middle py-2">
          {to ? (
            <Link
              to={to}
              className="block hover:opacity-80 transition-opacity"
              aria-label={`Open ${label} on ${d.iso}`}
            >
              {cell}
            </Link>
          ) : (
            cell
          )}
        </td>
      );
    })}
  </tr>
);

export default ReadingPlansHub;
